export default class BulkEditModalController {
    /* @ngInject */
    constructor($scope, DialogService, parentCtrl) {
        this.$scope = $scope;
        this.DialogService = DialogService;
        this.parentCtrl = parentCtrl;
        
        // Initialize preview state - always show when data is available
        
        // Initialize drag state
        this.dragOverActive = false;
        
        // Initialize download state
        this.isLoadingContacts = false;
        this.contactsLoadProgress = { current: 0, total: 0 };
        
        // Initialize project expansion state (all projects start collapsed)
        this.expandedProjects = new Set();
        
        // Prevent default drag behaviors on the document to ensure drop works
        this.setupDocumentDragPrevention();
        
        // Start loading contacts when modal opens
        this.startLoadingContacts();
    }
    
    // Getter methods to always return current parent state
    get importErrors() {
        return this.parentCtrl.importErrors || [];
    }
    
    get importPreviewData() {
        return this.parentCtrl.importPreviewData;
    }
    
    get showPreview() {
        // Always show preview if we have import preview data
        return this.parentCtrl.importPreviewData !== null;
    }
    
    get isLoadingProjects() {
        return this.parentCtrl.loadingOrganizationProjects;
    }
    
    get isImporting() {
        return this.parentCtrl.importInProgress;
    }
    
    get hasChanges() {
        if (!this.importPreviewData) return false;
        return this.importPreviewData.toAdd.length > 0 || 
               this.importPreviewData.toUpdate.length > 0 || 
               this.importPreviewData.toRemove.length > 0;
    }
    
    get importResults() {
        return this.parentCtrl.importResults;
    }
    
    setupDocumentDragPrevention() {
        const preventDefault = (e) => {
            // Only prevent default if the event is NOT on our drop zone
            if (!e.target.closest('[drag-drop]')) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        
        // Add event listeners to document
        document.addEventListener('dragenter', preventDefault, false);
        document.addEventListener('dragover', preventDefault, false);
        document.addEventListener('drop', preventDefault, false);
        
        // Clean up on scope destroy
        this.$scope.$on('$destroy', () => {
            document.removeEventListener('dragenter', preventDefault, false);
            document.removeEventListener('dragover', preventDefault, false);
            document.removeEventListener('drop', preventDefault, false);
        });
    }
    
    closeDialog() {
        this.DialogService.cancel();
    }
    
    // Project expansion methods
    toggleProjectExpansion(projectName) {
        if (this.expandedProjects.has(projectName)) {
            this.expandedProjects.delete(projectName);
        } else {
            this.expandedProjects.add(projectName);
        }
    }
    
    isProjectExpanded(projectName) {
        return this.expandedProjects.has(projectName);
    }
    
    // Helper methods for phone number parsing (to avoid regex in templates)
    getPhoneCountryCode(phoneNumber) {
        if (!phoneNumber) return '';
        const match = phoneNumber.match(/(\d+)/);
        return match ? phoneNumber.substring(0, phoneNumber.indexOf(match[0])) : '';
    }
    
    getPhoneNumberDigits(phoneNumber) {
        if (!phoneNumber) return '';
        const match = phoneNumber.match(/(\d+)/);
        return match ? phoneNumber.substring(phoneNumber.indexOf(match[0])) : '';
    }
    
    async startLoadingContacts() {
        if (this.parentCtrl.loadingOrganizationProjects) {
            // Wait for organization projects to load first
            return;
        }
        
        this.isLoadingContacts = true;
        this.contactsLoadProgress = { current: 0, total: this.parentCtrl.contactGroups.length };
        this.$scope.$applyAsync();
        
        try {
            // Load contacts for all contact groups in parallel
            const loadPromises = this.parentCtrl.contactGroups.map(async (contactGroup, index) => {
                if (!this.parentCtrl.contactGroupContacts.has(contactGroup.name)) {
                    await this.parentCtrl.loadContactsForGroup(contactGroup);
                }
                
                this.contactsLoadProgress.current = index + 1;
                this.$scope.$applyAsync();
                return contactGroup;
            });
            
            await Promise.allSettled(loadPromises);
        } catch (error) {
            // Log error to parent controller or handle appropriately
            if (this.parentCtrl.handleError) {
                this.parentCtrl.handleError('Error loading contacts for export:', error);
            }
        } finally {
            this.isLoadingContacts = false;
            this.$scope.$applyAsync();
        }
    }

    async downloadCSV() {
        // If still loading, wait for it to complete
        if (this.isLoadingContacts) {
            return;
        }
        
        await this.parentCtrl.exportContactsCSV();
    }
    
    goBack() {
        // Clear the import data and errors to return to the initial view
        this.parentCtrl.csvImportData = null;
        this.parentCtrl.importErrors = [];
        this.parentCtrl.importPreviewData = null;
        this.parentCtrl.importResults = null;
        this.$scope.$applyAsync();
    }
    
    handleDrop(event, files) {
        this.dragOverActive = false;
        
        if (files && files.length > 0) {
            this.parentCtrl.handleCSVFile(files[0]);
        } else if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            // Fallback: try to get files from event
            this.parentCtrl.handleCSVFile(event.dataTransfer.files[0]);
        }
        
        // Force scope update to reflect any state changes
        this.$scope.$applyAsync();
    }
    
    handleDragOver() {
        this.dragOverActive = true;
        this.$scope.$applyAsync();
    }
    
    handleDragLeave() {
        this.dragOverActive = false;
        this.$scope.$applyAsync();
    }
    
    triggerFileInput() {
        document.getElementById('bulkEditFileInput').click();
    }
    
    handleFileInput(event) {
        const file = event.target.files[0];
        if (file) {
            this.parentCtrl.handleCSVFile(file);
            // Force scope update to reflect any state changes
            this.$scope.$applyAsync();
        }
        // Reset the input value so the same file can be selected again
        event.target.value = '';
    }
    
    async applyImport() {
        // Apply the import without showing toast messages
        await this.parentCtrl.applyCSVImport(false);
        
        // Check if there were any errors
        if (this.parentCtrl.importResults && this.parentCtrl.importResults.errors.length > 0) {
            // Keep modal open to show errors
            this.$scope.$applyAsync();
        } else {
            // Close modal on successful completion
            this.DialogService.hide();
            // Show success toast since modal is closing
            const results = this.parentCtrl.importResults;
            if (results) {
                let message = '';
                if (results.added > 0) message += `Added ${results.added} contact${results.added > 1 ? 's' : ''}. `;
                if (results.updated > 0) message += `Updated ${results.updated} contact${results.updated > 1 ? 's' : ''}. `;
                if (results.removed > 0) message += `Removed ${results.removed} contact${results.removed > 1 ? 's' : ''}. `;
                this.parentCtrl.ToastService.showSimpleText(message || 'Import completed successfully');
            }
        }
    }
    

} 